import React, { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react'
import { cn } from '@lair-finance/util'
import { cva } from 'class-variance-authority'
import { AsProp } from '@/shared/types'

const buttonVariant = cva(
  'inline-flex justify-center items-center border rounded-[8px] disabled:bg-greyscale-300 disabled:border-[transparent] disabled:text-greyscale-700 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        xs: 'h-[24px] px-[7px] typo-label-3',
        sm: 'h-[32px] px-[15px] typo-label-3 gap-1',
        md: 'h-[40px] px-[15px] typo-label-2 gap-2',
        lg: 'h-[48px] px-[31px] typo-label-2 gap-3',
        xl: 'h-[80px] px-[31px] typo-headline-1 gap-4',
      },
      color: {
        primary: 'bg-primary-500 border-primary-500',
        secondary: 'bg-primary-500 border-transparent bg-opacity-15 text-primary-500',
        black: 'bg-greyscale-600 border-greyscale-600',
        white: 'bg-greyscale-100 border-greyscale-100 text-greyscale-900',
        grey: 'bg-greyscale-300 border-greyscale-300 text-greyscale-700',
        darkGreen: 'bg-palette-dark-green border-palette-dark-green',
        transparent: 'bg-transparent border-transparent text-palette-sky',
      },
      fullWidth: {
        true: 'w-full',
      },
      hoverable: {
        true: 'hover:bg-greyscale-100 hover:border-greyscale-100 hover:text-greyscale-900 group-hover:bg-greyscale-100 group-hover:border-greyscale-100 group-hover:text-greyscale-900 group-hover:rounded-none',
      },
      active: {
        true: 'border-greyscale-100 text-greyscale-100',
      },
      icon: { true: '', false: '' },
    },
    compoundVariants: [
      { size: 'sm', icon: true, className: 'pl-[7px]' },
      { size: 'md', icon: true, className: 'pl-[11px]' },
      { size: 'lg', icon: true, className: 'pl-[23px]' },
      { size: 'xl', icon: true, className: 'pl-[23px]' },
    ],
  },
)

const borderGradientVariant = cva(
  'group inline-flex-center rounded-[8px] p-px overflow-hidden hover:bg-none hover:border hover:border-greyscale-100 hover:p-0',
  {
    variants: {
      color: { purple: 'bg-[linear-gradient(90deg,#B5AEFF_0%,#6A5CFF_100%)]' },
    },
  },
)

type ElementProps<T extends React.ElementType, Props = {}> = AsProp<T> &
  Omit<React.ComponentPropsWithoutRef<T>, 'as' | keyof Props> &
  (T extends 'a' ? AnchorHTMLAttributes<HTMLAnchorElement> : ButtonHTMLAttributes<HTMLButtonElement>) &
  Props

export type ButtonProps<T extends React.ElementType = 'button'> = ElementProps<
  T,
  {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    color?: 'primary' | 'secondary' | 'darkGreen' | 'black' | 'white' | 'grey' | 'transparent'
    borderGradient?: 'purple'
    borderGradientClass?: string
    icon?: React.ReactNode
    fullWidth?: boolean
    active?: boolean
    hoverable?: boolean
    as?: React.ElementType
  }
>
type ButtonComponent = <C extends React.ElementType = 'button'>(props: ButtonProps<C>) => React.ReactElement | null

export const Button = React.forwardRef(
  <T extends React.ElementType = 'button'>(
    {
      className,
      size = 'md',
      color = 'primary',
      borderGradient,
      borderGradientClass,
      icon,
      fullWidth,
      active,
      hoverable = true,
      children,
      as,
      ...props
    }: ButtonProps<T>,
    ref: React.Ref<React.ElementType<T>>,
  ) => {
    const Component: React.ElementType = as || 'button'
    const buttonClass = cn(
      buttonVariant({
        size,
        fullWidth,
        active,
        hoverable,
        icon: Boolean(icon),
        color,
      }),
      className,
    )

    if (borderGradient) {
      return (
        <Component className={cn(borderGradientVariant({ color: borderGradient }), borderGradientClass)} ref={ref} {...props}>
          <div className={buttonClass}>
            {icon}
            {children}
          </div>
        </Component>
      )
    }

    return (
      <Component className={buttonClass} ref={ref} {...props}>
        {icon}
        {children}
      </Component>
    )
  },
) as ButtonComponent
